import React, { useState } from "react";
import axios from "axios";
import { BookOpen, Download, Loader2, AlertCircle, Check, FileText, Scale, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const highlights = [
  { Icon: Scale, text: "Your rights as a litigant in person" },
  { Icon: FileText, text: "Court bundle & position statement tips" },
  { Icon: Shield, text: "What a McKenzie Friend can — and cannot — do" },
];

const LeadMagnet = () => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState(null);

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Please enter a valid email.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const triggerDownload = async () => {
    const url = `${BACKEND}/api/guide/download`;
    try {
      // Fetch the PDF as a blob to bypass popup blockers
      const res = await fetch(url, { credentials: "omit" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = "McKenzie-Friend-Guide-Michael-Skierniewski.pdf";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
    } catch (e) {
      // Hard fallback – open in same tab
      window.location.href = url;
    }
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setServerError(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/lead-magnet`, {
        name: form.name.trim(),
        email: form.email.trim(),
        consent: true,
      });
      await triggerDownload();
      setDone(true);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || "Something went wrong.";
      setServerError(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadAgain = () => {
    triggerDownload();
  };

  return (
    <section
      id="guide"
      data-testid="lead-magnet-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 grain-overlay opacity-50" aria-hidden="true" />
      <div className="absolute inset-0 warm-glow" aria-hidden="true" />

      <div className="relative max-w-6xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left – book mock */}
          <div className="lg:col-span-5">
            <div className="relative mx-auto max-w-xs">
              {/* Book cover */}
              <div
                data-testid="lead-magnet-cover"
                className="relative bg-[#1A1918] border border-[#C89D54]/40 aspect-[3/4] p-8 flex flex-col shadow-[0_30px_70px_-20px_rgba(0,0,0,0.7)] rotate-[-3deg] hover:rotate-0 transition-transform duration-700"
              >
                <div className="absolute inset-y-0 left-0 w-1.5 bg-[#C89D54]" />
                <div className="overline text-[10px] mb-3">A Free Guide</div>
                <div className="font-serif-display text-[#F4F0EB] text-3xl leading-[1.05]">
                  What is a
                  <span className="block italic text-[#C89D54]">
                    McKenzie Friend?
                  </span>
                </div>
                <div className="mt-3 text-[11px] text-[#A6A098] leading-snug">
                  Your rights, your options &amp; what to expect in the UK Family
                  Court.
                </div>
                <div className="mt-auto pt-6 border-t border-[#C89D54]/30">
                  <div className="text-[10px] uppercase tracking-widest text-[#C89D54]">
                    by
                  </div>
                  <div className="font-serif-display text-base text-[#F4F0EB] mt-0.5">
                    Michael Skierniewski
                  </div>
                  <div className="text-[10px] text-[#A6A098] italic mt-0.5">
                    Professional McKenzie Friend
                  </div>
                </div>
                <BookOpen
                  size={42}
                  className="absolute -top-5 -right-5 text-[#C89D54] bg-[#1A1918] p-2 border border-[#C89D54]/40"
                  strokeWidth={1.4}
                />
              </div>
              <div className="absolute -bottom-3 left-6 right-6 h-3 bg-[#C89D54]/30 blur-sm" />
            </div>
          </div>

          {/* Right – pitch + form */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#C89D54]" />
              <span className="overline">Free Download</span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#F4F0EB] tracking-tight leading-[1.05]">
              Know your rights
              <span className="block italic text-[#C89D54]">before you walk in.</span>
            </h2>
            <p className="mt-6 font-sans-body text-base md:text-lg text-[#A6A098] leading-relaxed max-w-xl">
              A plain-English 7-chapter guide to representing yourself in the
              Family Court — what a McKenzie Friend does, what the law says,
              and how to prepare for your first hearing. Written from 15 years
              of practical courtroom experience.
            </p>

            <ul className="mt-8 space-y-3">
              {highlights.map(({ Icon, text }) => (
                <li
                  key={text}
                  data-testid="lead-magnet-highlight"
                  className="flex items-center gap-3 text-sm text-[#F4F0EB]"
                >
                  <span className="h-8 w-8 shrink-0 flex items-center justify-center border border-[#C89D54]/40">
                    <Icon size={14} className="text-[#C89D54]" />
                  </span>
                  {text}
                </li>
              ))}
            </ul>

            {done ? (
              <div
                data-testid="lead-magnet-success"
                className="mt-10 border border-[#C89D54]/40 bg-[#262321] p-6 md:p-7"
              >
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 shrink-0 rounded-full bg-[#C89D54]/15 border border-[#C89D54]/50 flex items-center justify-center">
                    <Check size={18} className="text-[#C89D54]" strokeWidth={2.2} />
                  </div>
                  <div className="flex-1">
                    <div className="font-serif-display text-2xl text-[#F4F0EB] leading-tight">
                      Your guide is on its way.
                    </div>
                    <p className="mt-2 text-sm text-[#A6A098] leading-relaxed">
                      The PDF download has started. If nothing happened,{" "}
                      <button
                        type="button"
                        onClick={downloadAgain}
                        data-testid="lead-magnet-redownload"
                        className="text-[#C89D54] underline hover:text-[#DDB46F]"
                      >
                        click here to download again
                      </button>
                      .
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <form
                data-testid="lead-magnet-form"
                onSubmit={handleSubmit}
                className="mt-10 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-start"
                noValidate
              >
                <div>
                  <input
                    data-testid="lead-magnet-name"
                    type="text"
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, name: e.target.value }))
                    }
                    className={`w-full bg-[#262321] border ${
                      errors.name ? "border-[#E08F73]/60" : "border-[#C89D54]/30"
                    } text-[#F4F0EB] placeholder:text-[#A6A098]/50 px-4 py-3 text-sm focus:outline-none focus:border-[#C89D54]`}
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-[#E08F73] flex items-center gap-1">
                      <AlertCircle size={11} /> {errors.name}
                    </p>
                  )}
                </div>
                <div>
                  <input
                    data-testid="lead-magnet-email"
                    type="email"
                    placeholder="Your email"
                    value={form.email}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, email: e.target.value }))
                    }
                    className={`w-full bg-[#262321] border ${
                      errors.email ? "border-[#E08F73]/60" : "border-[#C89D54]/30"
                    } text-[#F4F0EB] placeholder:text-[#A6A098]/50 px-4 py-3 text-sm focus:outline-none focus:border-[#C89D54]`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-[#E08F73] flex items-center gap-1">
                      <AlertCircle size={11} /> {errors.email}
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  data-testid="lead-magnet-submit"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      <Download size={16} /> Get the Guide
                    </>
                  )}
                </button>

                {serverError && (
                  <div
                    data-testid="lead-magnet-error"
                    className="sm:col-span-3 flex items-start gap-3 border border-[#E08F73]/50 bg-[#E08F73]/10 px-4 py-3 text-sm text-[#F4F0EB]"
                  >
                    <AlertCircle size={14} className="text-[#E08F73] shrink-0 mt-0.5" />
                    <span>{serverError}</span>
                  </div>
                )}

                <p className="sm:col-span-3 text-xs text-[#A6A098]/70 leading-relaxed">
                  Free, instant download. Your email will only be used to
                  contact you about Family Court support — never shared, never
                  spammed.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadMagnet;
